import { useState } from "react";
import { MIN_PASSWORD_LENGTH } from "@edu/shared";
import { ApiError, changePassword } from "../../lib/api.ts";
import { Dialog } from "../shell/Dialog.tsx";
import { SESSION_PARTIAL_TEXT } from "./auth-errors.ts";

/**
 * Смена своего пароля.
 *
 * Окном, а не отдельным экраном профиля, потому что экрана профиля нет вовсе:
 * заводить целый раздел ради одного действия, которое делают раз в год, значит
 * добавить в сайдбар пункт, где всегда одна кнопка. Когда профиль появится
 * (имя, пояс, привязка входов), окно переедет туда без изменений: оно ничего не
 * знает ни про сайдбар, ни про адрес.
 *
 * ⚠️ Пункт, открывающий это окно, виден только тому, у кого вход по паролю есть
 * (`me.hasPassword`). Человеку, заведённому через Google, сервер отвечает 404
 * `no_password_login`, и показать ему дверь, за которой отказ, было бы хуже, чем
 * не показывать её вовсе.
 */

/** Отдельно от разметки: коды ошибок это контракт с API, а не текст кнопки. */
function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    // Ровно как на входе: сервер не различает «нет такого» и «не сошлось», и
    // интерфейс не выдумывает различия, которого нет
    if (err.code === "invalid_credentials") return "Текущий пароль не подошёл.";
    if (err.code === "weak_password") {
      return `Новый пароль короче ${MIN_PASSWORD_LENGTH} символов.`;
    }
    if (err.code === "same_password") return "Новый пароль совпадает с текущим.";
    // Вкладка с устаревшим признаком полноты (9.6 docs/account-adopt-design.md):
    // повтор не пройдёт, сказать надо, где пароль поменять можно
    if (err.code === "session_partial") return SESSION_PARTIAL_TEXT;
    if (err.code === "no_password_login") {
      return "У этого аккаунта нет входа по паролю: вы заходите через сервис.";
    }
    if (err.code === "too_many_attempts" || err.status === 429) {
      return "Слишком много попыток. Попробуйте через час.";
    }
  }
  return err instanceof Error ? err.message : String(err);
}

export function ChangePasswordDialog(props: { onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Сколько чужих сессий отозвано. `null` пока не сменили. */
  const [revoked, setRevoked] = useState<number | null>(null);

  /**
   * Три условия проверяются ЗДЕСЬ, а не только на сервере, и это не дублирование
   * правил, а разное назначение. Сервер отвечает отказом на запрос, форма же
   * гасит кнопку до запроса: круг по сети при 128 кбит/с ради «пароли не
   * совпали» это несколько секунд, за которые человек успевает решить, что
   * ничего не работает. Единственное общее число вынесено в `@edu/shared`, чтобы
   * граница длины не разъехалась между формой и сервером.
   */
  const tooShort = next.normalize("NFC").length < MIN_PASSWORD_LENGTH;
  const mismatch = again.length > 0 && next !== again;
  const ready = current.length > 0 && !tooShort && next === again && !busy;

  async function submit() {
    if (!ready) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await changePassword(current, next);
      setRevoked(res.revoked);
      // Оба поля стираем сразу: окно остаётся открытым ради строки об отозванных
      // сессиях, а оставленный на экране пароль это тот же пароль на чужом экране
      setCurrent("");
      setNext("");
      setAgain("");
    } catch (e) {
      setErr(messageFor(e));
      setCurrent("");
      setBusy(false);
      return;
    }
    setBusy(false);
  }

  /*
    ⚠️ **Подвал окна берёт кнопки у обеих веток** (14.09.2026, долг подхода 2ж).
    Ряд кнопок стоял ВНУТРИ тела, потому что тело здесь это настоящая `<form>` с
    `type="submit"` и `autocomplete`, а выносить кнопку из формы нельзя: вместе с
    ней уезжает отправка по Enter и обновление записи в менеджере паролей. Теперь
    форму держит само окно (`onSubmit`), то есть она обнимает и тело, и подвал, и
    кнопка остаётся своей.

    Настоящая форма нужна ровно ради менеджера паролей: без неё он не предложит
    сгенерировать пароль и, что хуже, не обновит уже сохранённый, и человек
    останется с записанным старым.
  */
  const done = revoked !== null;

  return (
    <Dialog
      title="Смена пароля"
      note={done ? undefined : "потребуется текущий пароль"}
      onSubmit={
        done
          ? undefined
          : (e) => {
              e.preventDefault();
              void submit();
            }
      }
      foot={
        done ? (
          <button type="button" className="btn btn--primary" onClick={props.onClose}>
            Готово
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn--quiet"
              onClick={props.onClose}
              disabled={busy}
            >
              Отмена
            </button>
            <button type="submit" className="btn btn--primary" disabled={!ready}>
              Сменить пароль
            </button>
          </>
        )
      }
      onClose={props.onClose}
    >
      {done ? (
        <div className="form">
          <p className="form__done">Пароль изменён.</p>
          <p className="muted">
            {/*
              Про отозванные сессии говорим ЯВНО и числом. Молчание здесь стоило
              бы звонка «у меня на телефоне всё разлогинилось»: отзыв чужих
              сессий это половина смысла смены пароля, и человек должен узнать о
              нём от нас, а не от телефона.
            */}
            {revoked === 0
              ? "Других устройств с этим аккаунтом не было. Здесь вы остаётесь в системе."
              : `Остальные устройства (${revoked}) выведены из системы. Здесь вы остаётесь в системе.`}
          </p>
        </div>
      ) : (
        <div className="form">
          {/*
            Скрытое поле логина: без него менеджер паролей не понимает, У КОГО
            меняется пароль, и либо не предлагает обновить запись, либо заводит
            вторую. Значения у нас нет (логин на клиенте не хранится), поэтому
            поле пустое и невидимое, но сам факт его наличия менеджерам нужен.
          */}
          <input type="text" name="username" autoComplete="username" hidden readOnly value="" />

          <label className="field">
            <span className="lbl">Текущий пароль</span>
            <input
              className="fld"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              type="password"
              name="current-password"
              autoComplete="current-password"
              maxLength={1024}
              autoFocus
            />
          </label>

          <label className="field">
            <span className="lbl">Новый пароль</span>
            <input
              className="fld"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              type="password"
              name="new-password"
              autoComplete="new-password"
              maxLength={1024}
            />
          </label>
          {/*
            Требование к длине показано ВСЕГДА, а не после неудачной попытки:
            правило, о котором узнаёшь из отказа, воспринимается как придирка
          */}
          <p className="hint">Не короче {MIN_PASSWORD_LENGTH} символов.</p>

          <label className="field">
            <span className="lbl">Новый пароль ещё раз</span>
            <input
              className="fld"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
              type="password"
              name="new-password-confirm"
              autoComplete="new-password"
              maxLength={1024}
              aria-invalid={mismatch}
            />
          </label>
          {/*
            Подтверждение здесь по той же причине, по которой оно есть в скрипте
            `set-password`: опечатка в невидимом поле обнаруживается только при
            следующем входе, и проверить её уже нечем, в базе scrypt-хеш.

            Строка под полем, а не общая строка отказа формы: она говорит про
            это поле и снимается, как только оно исправлено
          */}
          {mismatch && <p className="hint hint--err">Пароли не совпадают.</p>}

          {err !== null && <p className="err">{err}</p>}
        </div>
      )}
    </Dialog>
  );
}
